import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { ArrowLeftIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { categoriesAPI, transactionsAPI } from '../services/api';
import SearchableSelect from './SearchableSelect';
import StatusToast from './StatusToast';
import { parseBusinessDate, toBusinessIsoDate } from '../utils/businessDate';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toComparableText = (value) => String(value || '').toLowerCase().trim();
const RECEIPT_DETAIL_STORAGE_KEY = 'receipt-tracker:receipt-detail:v1';

const parseTransactionDate = (value) => parseBusinessDate(value);
const toIsoDate = (value) => toBusinessIsoDate(value);

const buildReceiptKey = (transaction) => {
  if (transaction.receiptImageUrl) {
    return `receipt:${transaction.receiptImageUrl}`;
  }

  const fallbackDate = toIsoDate(transaction.transactionDate) || 'unknown-date';
  const fallbackStore = toComparableText(transaction.storeName) || 'unknown-store';
  const fallbackLocation = toComparableText(transaction.storeLocation) || 'unknown-location';
  const fallbackPayment = toComparableText(transaction.paymentMethod) || 'unknown-payment';

  return `meta:${fallbackDate}|${fallbackStore}|${fallbackLocation}|${fallbackPayment}`;
};

const normalizeMetaKeySegment = (value, unknownToken) => {
  const normalized = String(value || '').trim();
  return normalized === unknownToken ? '' : normalized;
};

const parseMetaReceiptKey = (value) => {
  const normalizedValue = String(value || '');
  if (!normalizedValue.startsWith('meta:')) {
    return null;
  }

  const segments = normalizedValue.slice(5).split('|');
  return {
    transactionDate: normalizeMetaKeySegment(segments[0], 'unknown-date'),
    storeName: normalizeMetaKeySegment(segments[1], 'unknown-store'),
    storeLocation: normalizeMetaKeySegment(segments[2], 'unknown-location'),
    paymentMethod: normalizeMetaKeySegment(segments[3], 'unknown-payment'),
  };
};

const readStoredReceiptSelection = (receiptKey) => {
  if (typeof window === 'undefined' || !receiptKey) {
    return null;
  }
  try {
    const rawValue = window.sessionStorage.getItem(RECEIPT_DETAIL_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }
    const parsed = JSON.parse(rawValue);
    if (String(parsed?.key || '') !== String(receiptKey || '')) {
      return null;
    }
    return parsed?.receipt || null;
  } catch {
    return null;
  }
};

const persistStoredReceiptSelection = (receipt) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (!receipt?.key) {
      window.sessionStorage.removeItem(RECEIPT_DETAIL_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(
      RECEIPT_DETAIL_STORAGE_KEY,
      JSON.stringify({
        key: String(receipt.key),
        receipt,
        savedAt: Date.now(),
      })
    );
  } catch {
    // Ignore storage errors and continue with in-memory navigation state.
  }
};

const findReceiptByRouteKey = (groupedReceipts, routeKey) => {
  const receipts = Array.isArray(groupedReceipts) ? groupedReceipts : [];
  const normalizedRouteKey = String(routeKey || '');
  const exactMatch = receipts.find((group) => String(group.key) === normalizedRouteKey);
  if (exactMatch) {
    return exactMatch;
  }

  const parsedMetaKey = parseMetaReceiptKey(normalizedRouteKey);
  if (!parsedMetaKey) {
    return null;
  }

  return receipts.find((group) => (
    String(toIsoDate(group.transactionDate) || '').trim() === parsedMetaKey.transactionDate
    && toComparableText(group.storeName) === toComparableText(parsedMetaKey.storeName)
    && toComparableText(group.storeLocation) === toComparableText(parsedMetaKey.storeLocation)
    && toComparableText(group.paymentMethod) === toComparableText(parsedMetaKey.paymentMethod)
  )) || null;
};

const groupTransactionsByReceipt = (transactions) => {
  const groups = new Map();

  (transactions || []).forEach((transaction) => {
    const key = buildReceiptKey(transaction);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        receiptImageUrl: transaction.receiptImageUrl || '',
        transactionDate: transaction.transactionDate,
        storeName: transaction.storeName,
        storeLocation: transaction.storeLocation,
        paymentMethod: transaction.paymentMethod,
        taxAmount: toNumber(transaction.taxAmount),
        hasTaxLineItem: false,
        items: [],
        totalAmount: 0,
      });
    }

    const group = groups.get(key);
    group.items.push(transaction);
    group.totalAmount = Number((group.totalAmount + toNumber(transaction.itemTotal)).toFixed(2));
    if (/\btax\b/i.test(String(transaction.itemName || ''))) {
      group.hasTaxLineItem = true;
    }

    const currentDate = parseTransactionDate(group.transactionDate).getTime();
    const transactionDate = parseTransactionDate(transaction.transactionDate).getTime();
    if (Number.isFinite(transactionDate) && (!Number.isFinite(currentDate) || transactionDate > currentDate)) {
      group.transactionDate = transaction.transactionDate;
    }
  });

  return Array.from(groups.values());
};

const ADD_CATEGORY_OPTION = '__add_category__';
const createEditableReceiptDraft = (receipt) => ({
  transactionDate: toIsoDate(receipt?.transactionDate) || '',
  storeName: String(receipt?.storeName || ''),
  storeLocation: String(receipt?.storeLocation || ''),
  paymentMethod: String(receipt?.paymentMethod || ''),
  items: (receipt?.items || []).map((transaction) => ({
    id: String(transaction.id),
    itemName: String(transaction.itemName || ''),
    quantity: String(
      Number.isFinite(Number(transaction.quantity)) && Number(transaction.quantity) > 0
        ? Number(transaction.quantity)
        : 1
    ),
    itemTotal: String(Number(toNumber(transaction.itemTotal).toFixed(2))),
    categoryId: String(transaction.categoryId || ''),
  })),
});

function ReceiptDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { receiptKey: encodedReceiptKey } = useParams();
  const navigationReceipt = location.state?.receipt || null;
  const queryReceiptKey = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return String(params.get('key') || '').trim();
  }, [location.search]);
  const receiptKey = useMemo(() => {
    if (queryReceiptKey) {
      return queryReceiptKey;
    }
    if (!encodedReceiptKey && navigationReceipt?.key) {
      return String(navigationReceipt.key);
    }
    try {
      return decodeURIComponent(String(encodedReceiptKey || ''));
    } catch {
      return String(encodedReceiptKey || '');
    }
  }, [encodedReceiptKey, navigationReceipt?.key, queryReceiptKey]);
  const storedReceipt = useMemo(() => readStoredReceiptSelection(receiptKey), [receiptKey]);
  const initialReceipt = navigationReceipt || storedReceipt || null;
  const savedHighlightTimeoutRef = useRef(null);
  const [transactions, setTransactions] = useState(() => (
    Array.isArray(initialReceipt?.items) ? initialReceipt.items : []
  ));
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(() => !Boolean(initialReceipt));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingTransactionId, setUpdatingTransactionId] = useState('');
  const [activeTransactionActionId, setActiveTransactionActionId] = useState('');
  const [savedTransactionId, setSavedTransactionId] = useState('');
  const [isEditingReceipt, setIsEditingReceipt] = useState(false);
  const [receiptEditStep, setReceiptEditStep] = useState(1);
  const [isEditingReceiptHeader, setIsEditingReceiptHeader] = useState(false);
  const [isEditingReceiptItems, setIsEditingReceiptItems] = useState(false);
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [receiptDraft, setReceiptDraft] = useState({
    transactionDate: '',
    storeName: '',
    storeLocation: '',
    paymentMethod: '',
    items: [],
  });

  useEffect(() => {
    if (Array.isArray(navigationReceipt?.items) && navigationReceipt.items.length > 0) {
      setTransactions(navigationReceipt.items);
    }
  }, [navigationReceipt]);

  useEffect(() => {
    const load = async () => {
      try {
        if (!initialReceipt) {
          setLoading(true);
        }
        const [transactionsData, categoriesData] = await Promise.all([
          transactionsAPI.getAll({ forceRefresh: true }),
          categoriesAPI.getAll({ forceRefresh: true }),
        ]);
        setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        setError('');
      } catch (loadError) {
        console.error('Failed to load receipt detail:', loadError);
        setError(loadError?.response?.data?.error || 'Failed to load receipt');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [encodedReceiptKey, queryReceiptKey, initialReceipt]);

  useEffect(() => {
    if (encodedReceiptKey || queryReceiptKey) {
      return;
    }
    if (navigationReceipt) {
      return;
    }
    navigate('/transactions', { replace: true });
  }, [encodedReceiptKey, queryReceiptKey, navigationReceipt, navigate]);

  useEffect(() => () => {
    if (savedHighlightTimeoutRef.current) {
      clearTimeout(savedHighlightTimeoutRef.current);
    }
  }, []);

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(amount || 0));

  const formatDate = (dateString) => {
    const date = parseTransactionDate(dateString);
    if (Number.isNaN(date.getTime())) {
      return 'Unknown date';
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const receipt = useMemo(() => {
    const grouped = groupTransactionsByReceipt(transactions);
    const matchedReceipt = findReceiptByRouteKey(grouped, receiptKey);
    if (matchedReceipt) {
      return matchedReceipt;
    }
    if (navigationReceipt && String(navigationReceipt.key || '') === String(receiptKey || '')) {
      return navigationReceipt;
    }
    if (storedReceipt && String(storedReceipt.key || '') === String(receiptKey || '')) {
      return storedReceipt;
    }
    return null;
  }, [transactions, receiptKey, navigationReceipt, storedReceipt]);
  const shouldOpenEditModeFromRoute = location.pathname.endsWith('/edit');

  useEffect(() => {
    if (receipt) {
      persistStoredReceiptSelection(receipt);
    }
  }, [receipt]);

  useEffect(() => {
    if (!receipt) {
      return;
    }
    if (shouldOpenEditModeFromRoute) {
      setError('');
      setSuccess('');
      setReceiptDraft(createEditableReceiptDraft(receipt));
      setReceiptEditStep(1);
      setIsEditingReceiptHeader(false);
      setIsEditingReceiptItems(false);
      setIsEditingReceipt(true);
      return;
    }
    if (!isEditingReceipt) {
      setReceiptDraft(createEditableReceiptDraft(receipt));
    }
  }, [receipt, shouldOpenEditModeFromRoute, isEditingReceipt]);

  const categoryDisplayNameById = useMemo(() => (
    new Map(
      (categories || []).map((category) => [String(category.id), String(category.name || 'Uncategorized')])
    )
  ), [categories]);
  const categoriesById = useMemo(
    () => new Map((categories || []).map((category) => [String(category.id), category])),
    [categories]
  );
  const topLevelCategories = useMemo(
    () =>
      (categories || [])
        .filter((category) => !category.parentCategoryId)
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [categories]
  );
  const childCategoriesByParentId = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((category) => {
      if (!category.parentCategoryId) {
        return;
      }
      const parentId = String(category.parentCategoryId);
      if (!map.has(parentId)) {
        map.set(parentId, []);
      }
      map.get(parentId).push(category);
    });

    map.forEach((list) => {
      list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    });
    return map;
  }, [categories]);
  const orderedCategoryOptions = useMemo(() => {
    const options = [];
    topLevelCategories.forEach((parent) => {
      options.push(parent);
      const children = childCategoriesByParentId.get(String(parent.id)) || [];
      options.push(...children);
    });

    const addedIds = new Set(options.map((category) => String(category.id)));
    const orphanedCategories = (categories || [])
      .filter((category) => !addedIds.has(String(category.id)))
      .sort((a, b) => {
        const parentA = categoriesById.get(String(a.parentCategoryId || ''));
        const parentB = categoriesById.get(String(b.parentCategoryId || ''));
        const parentCompare = String(parentA?.name || '').localeCompare(String(parentB?.name || ''));
        if (parentCompare !== 0) {
          return parentCompare;
        }
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
    options.push(...orphanedCategories);
    return options;
  }, [categories, topLevelCategories, childCategoriesByParentId, categoriesById]);
  const categorySelectOptions = useMemo(
    () => ([
      ...orderedCategoryOptions.map((category) => {
        const categoryId = String(category.id);
        if (category.parentCategoryId) {
          const parent = categoriesById.get(String(category.parentCategoryId));
          return {
            value: categoryId,
            label: String(category.name || 'Uncategorized'),
            description: parent?.name || 'Subcategory',
            indent: true,
            searchText: `${parent?.name || ''} ${category.name || ''}`,
          };
        }
        return {
          value: categoryId,
          label: String(category.name || 'Uncategorized'),
          searchText: String(category.name || ''),
        };
      }),
      { value: ADD_CATEGORY_OPTION, label: '+ Add Category', searchText: 'add category new category' },
    ]),
    [orderedCategoryOptions, categoriesById]
  );
  const quickCategoryIds = useMemo(() => {
    const sortedTransactions = [...transactions].sort(
      (a, b) =>
        parseTransactionDate(b.transactionDate).getTime() -
        parseTransactionDate(a.transactionDate).getTime()
    );
    const seen = new Set();
    const topIds = [];

    sortedTransactions.forEach((transaction) => {
      const categoryId = String(transaction.categoryId || '');
      if (!categoryId || seen.has(categoryId) || !categoryDisplayNameById.has(categoryId)) {
        return;
      }
      seen.add(categoryId);
      topIds.push(categoryId);
    });

    return topIds.slice(0, 3);
  }, [transactions, categoryDisplayNameById]);

  const activeActionTransaction = useMemo(
    () => (receipt?.items || []).find((item) => String(item.id) === String(activeTransactionActionId)) || null,
    [receipt, activeTransactionActionId]
  );

  const quickCategoryIdsForTransaction = (transaction) => (
    quickCategoryIds.filter((categoryId) => String(transaction.categoryId || '') !== categoryId)
  );

  const handleCategoryChange = async (transactionId, categoryId) => {
    const normalizedTransactionId = String(transactionId || '').trim();
    const normalizedCategoryId = String(categoryId || '').trim();
    if (!normalizedTransactionId || !normalizedCategoryId) {
      return;
    }

    setUpdatingTransactionId(normalizedTransactionId);
    setError('');
    setSuccess('');
    try {
      const updated = await transactionsAPI.update(normalizedTransactionId, { categoryId: normalizedCategoryId });
      setTransactions((current) =>
        current.map((transaction) => (
          String(transaction.id) === normalizedTransactionId ? { ...transaction, ...updated } : transaction
        ))
      );
      if (savedHighlightTimeoutRef.current) {
        clearTimeout(savedHighlightTimeoutRef.current);
      }
      setSavedTransactionId(normalizedTransactionId);
      savedHighlightTimeoutRef.current = setTimeout(() => {
        setSavedTransactionId('');
      }, 1200);
      setSuccess('Category updated.');
    } catch (updateError) {
      console.error('Error updating transaction category:', updateError);
      setError(updateError?.response?.data?.error || 'Failed to update category');
    } finally {
      setUpdatingTransactionId('');
    }
  };

  const handleCategoryDropdownSelect = async (selectedValue) => {
    const normalizedValue = String(selectedValue || '').trim();
    if (!normalizedValue || !activeActionTransaction) {
      return;
    }
    if (normalizedValue === ADD_CATEGORY_OPTION) {
      setError('Add Category from this page is not available yet. Use Transactions screen to create one.');
      return;
    }
    await handleCategoryChange(activeActionTransaction.id, normalizedValue);
    setActiveTransactionActionId('');
  };

  const handleDelete = async (transactionId) => {
    try {
      setError('');
      await transactionsAPI.delete(transactionId);
      setTransactions((current) => current.filter((transaction) => String(transaction.id) !== String(transactionId)));
      setActiveTransactionActionId('');
      setSuccess('Transaction deleted.');
    } catch (deleteError) {
      console.error('Error deleting transaction:', deleteError);
      setError(deleteError?.response?.data?.error || 'Failed to delete transaction');
    }
  };

  const startEditingReceipt = () => {
    if (!receipt) {
      return;
    }
    setError('');
    setSuccess('');
    navigate(`/transactions/receipt/${encodeURIComponent(receiptKey)}/edit`);
  };

  const cancelEditingReceipt = () => {
    setIsEditingReceipt(false);
    setReceiptEditStep(1);
    setIsEditingReceiptHeader(false);
    setIsEditingReceiptItems(false);
    if (receipt) {
      setReceiptDraft(createEditableReceiptDraft(receipt));
    }
    if (shouldOpenEditModeFromRoute) {
      navigate(`/transactions/receipt/${encodeURIComponent(receiptKey)}`, { replace: true });
    }
  };

  const handleReceiptDraftChange = (field, value) => {
    setReceiptDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleReceiptItemChange = (itemId, field, value) => {
    setReceiptDraft((current) => ({
      ...current,
      items: (current.items || []).map((item) => (
        String(item.id) === String(itemId) ? { ...item, [field]: value } : item
      )),
    }));
  };

  const saveReceipt = async () => {
    if (!receipt) {
      return;
    }

    const nextDate = String(receiptDraft.transactionDate || '').trim();
    const nextStoreName = String(receiptDraft.storeName || '').trim();
    const nextStoreLocation = String(receiptDraft.storeLocation || '').trim();
    const nextPaymentMethod = String(receiptDraft.paymentMethod || '').trim();

    if (!nextDate || !nextStoreName || !nextPaymentMethod) {
      setError('Receipt requires date, store name, and payment type.');
      setReceiptEditStep(1);
      setIsEditingReceiptHeader(true);
      return;
    }

    if (!Array.isArray(receiptDraft.items) || receiptDraft.items.length === 0) {
      setError('Receipt must include at least one item.');
      setReceiptEditStep(2);
      return;
    }

    const normalizedItems = [];
    for (let index = 0; index < receiptDraft.items.length; index += 1) {
      const item = receiptDraft.items[index];
      const itemName = String(item.itemName || '').trim();
      const quantity = Number(item.quantity);
      const itemTotal = Number(item.itemTotal);
      const categoryId = String(item.categoryId || '').trim();

      const missingItemFields = [];
      if (!itemName) missingItemFields.push('item name');
      if (!categoryId) missingItemFields.push('category');
      if (!Number.isFinite(quantity) || quantity <= 0) missingItemFields.push('quantity');
      if (!Number.isFinite(itemTotal) || itemTotal === 0) missingItemFields.push('amount');
      if (missingItemFields.length > 0) {
        setError(`Item ${index + 1} is missing: ${missingItemFields.join(', ')}.`);
        setReceiptEditStep(2);
        return;
      }

      normalizedItems.push({
        id: String(item.id),
        itemName,
        quantity: Math.max(1, Math.round(quantity)),
        itemTotal: Number(itemTotal.toFixed(2)),
        categoryId,
      });
    }

    try {
      setSavingReceipt(true);
      setError('');
      setSuccess('');

      const updates = await Promise.all(
        normalizedItems.map((item) =>
          transactionsAPI.update(item.id, {
            transactionDate: nextDate,
            storeName: nextStoreName,
            storeLocation: nextStoreLocation || null,
            paymentMethod: nextPaymentMethod,
            itemName: item.itemName,
            quantity: item.quantity,
            itemTotal: item.itemTotal,
            categoryId: item.categoryId,
          })
        )
      );
      const updatesById = new Map(updates.map((transaction) => [String(transaction.id), transaction]));

      setTransactions((current) =>
        current.map((transaction) => (
          updatesById.get(String(transaction.id)) || transaction
        ))
      );

      setIsEditingReceipt(false);
      setReceiptEditStep(1);
      setIsEditingReceiptHeader(false);
      setIsEditingReceiptItems(false);
      setSuccess('Receipt updated.');
      navigate('/transactions');
    } catch (saveError) {
      console.error('Failed to update receipt:', saveError);
      setError(saveError?.response?.data?.error || 'Failed to update receipt');
    } finally {
      setSavingReceipt(false);
    }
  };

  if (loading) {
    return <div className="py-10 text-center text-sm text-gray-600">Loading receipt...</div>;
  }

  if (!receipt) {
    return (
      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-sm text-gray-700">{error || 'Receipt not found.'}</p>
        <button
          type="button"
          onClick={() => navigate('/transactions')}
          className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          Back to Transactions
        </button>
      </div>
    );
  }

  const effectiveTax = receipt.hasTaxLineItem ? 0 : receipt.taxAmount;
  const receiptTotal = Number((receipt.totalAmount + effectiveTax).toFixed(2));
  const receiptDraftTransactionDateValue = receiptDraft.transactionDate
    ? new Date(`${receiptDraft.transactionDate}T00:00:00`)
    : null;
  const receiptDraftSubtotal = Number(((receiptDraft.items || []).reduce((sum, item) => sum + toNumber(item.itemTotal), 0)).toFixed(2));
  const receiptDraftTotal = Number((receiptDraftSubtotal + effectiveTax).toFixed(2));
  const hasHeaderChanges = (
    String(receiptDraft.transactionDate || '') !== String(toIsoDate(receipt.transactionDate) || '')
    || String(receiptDraft.storeName || '').trim() !== String(receipt.storeName || '').trim()
    || String(receiptDraft.storeLocation || '').trim() !== String(receipt.storeLocation || '').trim()
    || String(receiptDraft.paymentMethod || '').trim() !== String(receipt.paymentMethod || '').trim()
  );
  const summaryRows = [
    { label: 'Date', value: formatDate(receiptDraft.transactionDate || receipt.transactionDate) },
    { label: 'Merchant', value: receiptDraft.storeName || receipt.storeName || '-' },
    { label: 'Location', value: receiptDraft.storeLocation || receipt.storeLocation || '-' },
    { label: 'Payment', value: receiptDraft.paymentMethod || receipt.paymentMethod || '-' },
  ];

  return (
    <div className={`space-y-4 ${isEditingReceipt ? 'pb-28' : ''}`}>
      <div className="sticky top-16 z-20 rounded-lg border border-gray-200 bg-white/95 p-3 shadow-sm backdrop-blur">
        {isEditingReceipt ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={cancelEditingReceipt}
                className="inline-flex items-center gap-1 rounded border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back
              </button>
              <p className="text-xs text-gray-500">Step {receiptEditStep} of 2</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    {receiptDraft.storeName || receipt.storeName || 'Receipt'}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {formatDate(receiptDraft.transactionDate || receipt.transactionDate)}
                    {receiptDraft.paymentMethod ? ` | ${receiptDraft.paymentMethod}` : ''}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Total</p>
                  <p className="text-sm font-semibold text-gray-900">{formatCurrency(receiptDraftTotal)}</p>
                </div>
              </div>
            </div>
            {receiptEditStep === 1 ? (
              <>
                {isEditingReceiptHeader ? (
                  <>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <label className="text-xs font-medium text-gray-600">
                        Date
                        <DatePicker
                          selected={receiptDraftTransactionDateValue}
                          onChange={(date) => {
                            if (!date) {
                              handleReceiptDraftChange('transactionDate', '');
                              return;
                            }
                            const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
                            const localIsoDate = new Date(date.getTime() - timezoneOffsetMs).toISOString().split('T')[0];
                            handleReceiptDraftChange('transactionDate', localIsoDate);
                          }}
                          dateFormat="MM-dd-yyyy"
                          withPortal
                          portalId="root"
                          wrapperClassName="mt-1 w-full"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          placeholderText="Transaction Date"
                        />
                      </label>
                      <label className="text-xs font-medium text-gray-600">
                        Merchant
                        <input
                          type="text"
                          value={receiptDraft.storeName}
                          onChange={(event) => handleReceiptDraftChange('storeName', event.target.value)}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          placeholder="Store Name"
                        />
                      </label>
                      <label className="text-xs font-medium text-gray-600 md:col-span-2">
                        Location
                        <input
                          type="text"
                          value={receiptDraft.storeLocation}
                          onChange={(event) => handleReceiptDraftChange('storeLocation', event.target.value)}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          placeholder="Store Location"
                        />
                      </label>
                      <div className="md:col-span-2">
                        <p className="mb-1 text-xs font-medium text-gray-600">Payment</p>
                        <div className="inline-flex overflow-hidden rounded-full border border-gray-300 bg-white p-1">
                          <button
                            type="button"
                            onClick={() => handleReceiptDraftChange('paymentMethod', 'Credit Card')}
                            className={`rounded-full px-3 py-1 text-xs transition ${
                              receiptDraft.paymentMethod === 'Credit Card'
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            Credit Card
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReceiptDraftChange('paymentMethod', 'Cash')}
                            className={`rounded-full px-3 py-1 text-xs transition ${
                              receiptDraft.paymentMethod === 'Cash'
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            Cash
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 border-t border-gray-200 pt-3">
                      <button
                        type="button"
                        onClick={() => setIsEditingReceiptHeader(false)}
                        className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Review
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={saveReceipt}
                          disabled={savingReceipt || !hasHeaderChanges}
                          className="rounded border border-blue-200 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50 disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent"
                        >
                          {savingReceipt ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setReceiptEditStep(2)}
                          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
                        >
                          Items &gt;
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                      {summaryRows.map((row, index) => (
                        <div
                          key={row.label}
                          className={`flex items-start justify-between gap-4 px-4 py-3 ${
                            index < summaryRows.length - 1 ? 'border-b border-gray-100' : ''
                          }`}
                        >
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            {row.label}
                          </p>
                          <p className="max-w-[65%] whitespace-normal break-words text-sm font-normal text-gray-700 text-right">
                            {row.value}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between gap-2 border-t border-gray-200 pt-3">
                      <button
                        type="button"
                        onClick={() => setIsEditingReceiptHeader(true)}
                        className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Edit
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={saveReceipt}
                          disabled={savingReceipt || !hasHeaderChanges}
                          className="rounded border border-blue-200 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50 disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent"
                        >
                          {savingReceipt ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setReceiptEditStep(2)}
                          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
                        >
                          Items &gt;
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="space-y-3 border-t border-gray-200 pt-3">
                  {isEditingReceiptItems ? (
                    <>
                      {(receiptDraft.items || []).map((item, index) => (
                        <div
                          key={`edit-receipt-item-${item.id}`}
                          className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 bg-white p-3 md:grid-cols-[minmax(0,2.2fr)_minmax(4rem,0.7fr)_minmax(5rem,0.9fr)_minmax(0,2fr)]"
                        >
                          <input
                            type="text"
                            value={item.itemName}
                            onChange={(event) => handleReceiptItemChange(item.id, 'itemName', event.target.value)}
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            placeholder={`Item ${index + 1} name`}
                          />
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={item.quantity}
                            onChange={(event) => handleReceiptItemChange(item.id, 'quantity', event.target.value)}
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            placeholder="Qty"
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={item.itemTotal}
                            onChange={(event) => handleReceiptItemChange(item.id, 'itemTotal', event.target.value)}
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            placeholder="Amount"
                          />
                          <SearchableSelect
                            value={String(item.categoryId || '')}
                            onChange={(selectedValue) => handleReceiptItemChange(item.id, 'categoryId', selectedValue)}
                            options={categorySelectOptions}
                            placeholder="Search category"
                            ariaLabel="Search category for receipt item"
                            inputClassName="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-400 focus:outline-none"
                            menuClassName="max-h-56"
                          />
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                        {(receiptDraft.items || []).map((item, index) => (
                          <div
                            key={`receipt-item-summary-${item.id}`}
                            className={`flex items-start justify-between gap-4 px-4 py-3 ${
                              index < receiptDraft.items.length - 1 ? 'border-b border-gray-100' : ''
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-gray-900">{item.itemName || '-'}</p>
                              <p className="mt-0.5 text-xs text-gray-500">
                                Qty {item.quantity || '-'}
                                {' • '}
                                {categoryDisplayNameById.get(String(item.categoryId || '')) || 'Uncategorized'}
                              </p>
                            </div>
                            <p className="shrink-0 text-sm font-semibold text-gray-800">
                              {Number.isFinite(Number(item.itemTotal)) ? formatCurrency(item.itemTotal) : '-'}
                            </p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="sticky bottom-16 z-20 -mx-3 mt-4 border-t border-gray-200 bg-white/95 px-3 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur">
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => setReceiptEditStep(1)}
                      disabled={savingReceipt}
                      className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
                    >
                      Back
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setIsEditingReceiptItems((current) => !current)}
                        disabled={savingReceipt}
                        className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
                      >
                        {isEditingReceiptItems ? 'Review' : 'Edit'}
                      </button>
                      <button
                        type="button"
                        onClick={saveReceipt}
                        disabled={savingReceipt}
                        className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:bg-blue-400"
                      >
                        {savingReceipt ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => navigate('/transactions')}
                className="inline-flex items-center gap-1 rounded border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back
              </button>
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-500">{formatDate(receipt.transactionDate)}</p>
                <button
                  type="button"
                  onClick={startEditingReceipt}
                  className="inline-flex h-7 items-center rounded-full bg-gray-100 px-3 text-xs font-medium text-gray-700 transition hover:bg-gray-200"
                  aria-label="Edit receipt"
                  title="Edit receipt"
                >
                  Edit
                </button>
              </div>
            </div>
            <h1 className="mt-2 truncate text-base font-semibold text-gray-900">{receipt.storeName || 'Unknown store'}</h1>
            <p className="truncate text-sm text-gray-600">{receipt.storeLocation || '-'}</p>
          </>
        )}
      </div>

      {!isEditingReceipt && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
        <div className="md:hidden space-y-2 p-3">
          <div className="grid grid-cols-[minmax(0,2.4fr)_minmax(1.75rem,0.55fr)_minmax(3.75rem,0.85fr)_minmax(0,2.3fr)_auto] items-center gap-1.5 px-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
            <span>Item</span>
            <span className="text-center">Qty</span>
            <span className="text-center">Amt</span>
            <span className="text-center">Category</span>
            <span className="sr-only">Actions</span>
          </div>
          {receipt.items.map((transaction) => (
            <div
              key={transaction.id}
              className={`rounded-lg border bg-white p-2 transition ${
                savedTransactionId === String(transaction.id)
                  ? 'border-green-300 bg-green-50/60'
                  : 'border-gray-200'
              } ${
                activeTransactionActionId === transaction.id ? 'relative z-30' : ''
              }`}
            >
              <div className="grid grid-cols-[minmax(0,2.4fr)_minmax(1.75rem,0.55fr)_minmax(3.75rem,0.85fr)_minmax(0,2.3fr)_auto] items-center gap-1.5 text-xs">
                <p
                  className="min-w-0 overflow-hidden font-medium leading-4 text-gray-900 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]"
                  title={transaction.itemName}
                >
                  {transaction.itemName}
                </p>
                <p className="text-center text-gray-700 whitespace-nowrap">{transaction.quantity}</p>
                <p className="text-center font-medium text-gray-900 whitespace-nowrap">{formatCurrency(transaction.itemTotal)}</p>
                <span
                  className="min-w-0 overflow-hidden rounded-full bg-gray-100 px-2 py-1 text-center text-[10px] leading-4 text-gray-700 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]"
                  title={categoryDisplayNameById.get(String(transaction.categoryId || '')) || 'Uncategorized'}
                >
                  {categoryDisplayNameById.get(String(transaction.categoryId || '')) || 'Uncategorized'}
                </span>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setActiveTransactionActionId(String(transaction.id))}
                    className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    title="Transaction actions"
                    aria-label="Transaction actions"
                  >
                    <EllipsisVerticalIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-[42%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                <th className="w-16 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                <th className="w-28 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="w-[30%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="w-20 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {receipt.items.map((transaction) => (
                <tr
                  key={transaction.id}
                  className={`${savedTransactionId === String(transaction.id) ? 'bg-green-50/60' : ''} ${activeTransactionActionId === transaction.id ? 'relative z-30' : ''}`}
                >
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <p className="truncate" title={transaction.itemName}>
                      {transaction.itemName}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{transaction.quantity}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {formatCurrency(transaction.itemTotal)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex max-w-full items-center overflow-hidden rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700 text-ellipsis whitespace-nowrap"
                      title={categoryDisplayNameById.get(String(transaction.categoryId || '')) || 'Uncategorized'}
                    >
                      {categoryDisplayNameById.get(String(transaction.categoryId || '')) || 'Uncategorized'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="relative inline-block">
                      <button
                        type="button"
                        onClick={() => setActiveTransactionActionId(String(transaction.id))}
                        className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                        title="Transaction actions"
                        aria-label="Transaction actions"
                      >
                        <EllipsisVerticalIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm">
          <span className="text-gray-600 whitespace-nowrap">
            Subtotal: <span className="font-semibold text-gray-900">{formatCurrency(receipt.totalAmount)}</span>
          </span>
          <span className="text-gray-600 whitespace-nowrap">
            Tax: <span className="font-semibold text-gray-900">{formatCurrency(effectiveTax)}</span>
          </span>
          <span className="text-gray-600 whitespace-nowrap">
            Total: <span className="font-semibold text-gray-900">{formatCurrency(receiptTotal)}</span>
          </span>
        </div>
        </div>
      )}

      {activeActionTransaction && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4" onClick={() => setActiveTransactionActionId('')}>
          <div
            className="w-full max-w-md overflow-y-auto rounded-2xl border border-gray-200 bg-white p-4 shadow-xl max-h-[85vh]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-3">
              <p className="text-xs text-gray-500">Item</p>
              <p className="truncate text-sm font-medium text-gray-900" title={activeActionTransaction.itemName}>
                {activeActionTransaction.itemName}
              </p>
            </div>
            {quickCategoryIdsForTransaction(activeActionTransaction).length > 0 && (
              <div className="mb-3">
                <p className="mb-1 text-xs font-medium text-gray-500">Quick category</p>
                <div className="flex flex-wrap gap-1.5">
                  {quickCategoryIdsForTransaction(activeActionTransaction).map((categoryId) => (
                    <button
                      key={`quick-detail-${activeActionTransaction.id}-${categoryId}`}
                      type="button"
                      onClick={async () => {
                        await handleCategoryChange(activeActionTransaction.id, categoryId);
                        setActiveTransactionActionId('');
                      }}
                      disabled={updatingTransactionId === String(activeActionTransaction.id)}
                      className="rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                      {categoryDisplayNameById.get(String(categoryId)) || 'Uncategorized'}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <label className="mb-1 block text-xs font-medium text-gray-600">Change category</label>
            <SearchableSelect
              value={String(activeActionTransaction.categoryId || '')}
              onChange={handleCategoryDropdownSelect}
              options={categorySelectOptions}
              placeholder="Search category"
              ariaLabel="Search category for selected transaction"
              inputClassName="w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 focus:border-blue-400 focus:outline-none"
              menuClassName="max-h-64"
              disabled={updatingTransactionId === String(activeActionTransaction.id)}
            />
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleDelete(activeActionTransaction.id)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-center text-sm text-red-600 hover:bg-red-50"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setActiveTransactionActionId('')}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <StatusToast
        error={error}
        success={success}
        onClearError={() => setError('')}
        onClearSuccess={() => setSuccess('')}
      />
    </div>
  );
}

export default ReceiptDetail;
